import React from 'react';
import { supabase } from './supabaseClient';

export interface FeatureFlags {
  beta_features: boolean;
  advanced_analytics: boolean;
  ai_assistant: boolean;
  marketplace_access: boolean;
  smart_farming: boolean;
  carbon_credits: boolean;
  logistics_tracking: boolean;
  community_features: boolean;
  [key: string]: boolean;
}

export interface UserPreferences {
  preferences: Record<string, any>;
  notification_settings: {
    email: boolean;
    sms: boolean;
    push: boolean;
    weather_alerts: boolean;
    market_updates: boolean;
    task_reminders: boolean;
  };
  ui_preferences: {
    theme: 'light' | 'dark';
    language: string;
    currency: string;
    timezone: string;
    date_format: string;
  };
  privacy_settings: {
    profile_visible: boolean;
    farm_data_public: boolean;
    show_location: boolean;
    allow_messaging: boolean;
  };
  feature_flags: FeatureFlags;
}

class FeatureFlagService {
  private static instance: FeatureFlagService;
  private userFlags: FeatureFlags | null = null;
  private globalFlags: FeatureFlags = {
    beta_features: false,
    advanced_analytics: false,
    ai_assistant: true,
    marketplace_access: true,
    smart_farming: true,
    carbon_credits: false,
    logistics_tracking: true,
    community_features: true,
  };

  private constructor() {}

  public static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService();
    }
    return FeatureFlagService.instance;
  }

  /**
   * Load user-specific feature flags from the database
   */
  async loadUserFlags(userId: string): Promise<FeatureFlags> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('feature_flags')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error loading user feature flags:', error);
        return this.globalFlags;
      }

      this.userFlags = data?.feature_flags || this.globalFlags;
      return this.userFlags || this.globalFlags;
    } catch (error) {
      console.error('Error loading user feature flags:', error);
      return this.globalFlags;
    }
  }

  /**
   * Check if a specific feature is enabled for the current user
   */
  async isFeatureEnabled(featureName: keyof FeatureFlags, userId?: string): Promise<boolean> {
    try {
      // If we have user flags loaded and no specific user requested, use cached flags
      if (this.userFlags && !userId) {
        return this.userFlags[featureName] || false;
      }

      // If specific user requested, load their flags
      if (userId) {
        const userFlags = await this.loadUserFlags(userId);
        return userFlags[featureName] || false;
      }

      // Fallback to global flags
      return this.globalFlags[featureName] || false;
    } catch (error) {
      console.error(`Error checking feature flag ${featureName}:`, error);
      return false; // Default to disabled on error
    }
  }

  /**
   * Update user feature flags
   */
  async updateUserFlags(userId: string, flags: Partial<FeatureFlags>): Promise<boolean> {
    try {
      const currentFlags = this.userFlags || this.globalFlags;
      const { error } = await supabase
        .from('profiles')
        .update({
          feature_flags: {
            ...currentFlags,
            ...flags,
          },
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user feature flags:', error);
        return false;
      }

      // Update local cache
      this.userFlags = { ...currentFlags, ...flags } as FeatureFlags;
      return true;
    } catch (error) {
      console.error('Error updating user feature flags:', error);
      return false;
    }
  }

  /**
   * Enable a feature for a specific user (for testing or early access)
   */
  async enableFeatureForUser(userId: string, featureName: keyof FeatureFlags): Promise<boolean> {
    return this.updateUserFlags(userId, { [featureName]: true });
  }

  /**
   * Disable a feature for a specific user
   */
  async disableFeatureForUser(userId: string, featureName: keyof FeatureFlags): Promise<boolean> {
    return this.updateUserFlags(userId, { [featureName]: false });
  }

  /**
   * Enable a feature for all users (global rollout)
   */
  async enableFeatureGlobally(featureName: keyof FeatureFlags): Promise<boolean> {
    try {
      // Get all profiles and update them individually
      const { data: profiles, error: fetchError } = await supabase
        .from('profiles')
        .select('id, feature_flags');

      if (fetchError) {
        console.error('Error fetching profiles:', fetchError);
        return false;
      }

      // Update each profile
      const updates = profiles?.map(profile => {
        const currentFlags = profile.feature_flags || {};
        return supabase
          .from('profiles')
          .update({
            feature_flags: {
              ...currentFlags,
              [featureName]: true,
            },
          })
          .eq('id', profile.id);
      }) || [];

      const results = await Promise.all(updates);
      const hasErrors = results.some(result => result.error);

      if (hasErrors) {
        console.error('Error enabling feature globally for some users');
        return false;
      }

      // Update global flags
      this.globalFlags[featureName] = true;
      return true;
    } catch (error) {
      console.error('Error enabling feature globally:', error);
      return false;
    }
  }

  /**
   * Get all feature flags for a user
   */
  async getUserFlags(userId: string): Promise<FeatureFlags> {
    return this.loadUserFlags(userId);
  }

  /**
   * Get user preferences
   */
  async getUserPreferences(userId: string): Promise<UserPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          preferences,
          notification_settings,
          ui_preferences,
          privacy_settings,
          feature_flags
        `)
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error loading user preferences:', error);
        return null;
      }

      return data as UserPreferences;
    } catch (error) {
      console.error('Error loading user preferences:', error);
      return null;
    }
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(
    userId: string, 
    updates: Partial<UserPreferences>
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (error) {
        console.error('Error updating user preferences:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating user preferences:', error);
      return false;
    }
  }

  /**
   * Check if user has completed onboarding
   */
  async isOnboardingCompleted(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error checking onboarding status:', error);
        return false;
      }

      return data?.onboarding_completed || false;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return false;
    }
  }

  /**
   * Mark onboarding as completed
   */
  async completeOnboarding(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          onboarding_completed: true,
          onboarding_progress: {
            steps_completed: ['welcome', 'farm_setup', 'preferences'],
            current_step: 'completed',
            completed_at: new Date().toISOString(),
          },
        })
        .eq('id', userId);

      if (error) {
        console.error('Error completing onboarding:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      return false;
    }
  }

  /**
   * Get feature usage statistics (admin only)
   */
  async getFeatureUsageStats(): Promise<Record<string, number>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('feature_flags');

      if (error) {
        console.error('Error getting feature usage stats:', error);
        return {};
      }

      const stats: Record<string, number> = {};
      
      data?.forEach(profile => {
        const flags = profile.feature_flags || {};
        Object.keys(flags).forEach(feature => {
          if (flags[feature]) {
            stats[feature] = (stats[feature] || 0) + 1;
          }
        });
      });

      return stats;
    } catch (error) {
      console.error('Error getting feature usage stats:', error);
      return {};
    }
  }

  /**
   * Reset user flags to defaults
   */
  async resetUserFlags(userId: string): Promise<boolean> {
    return this.updateUserFlags(userId, this.globalFlags);
  }

  /**
   * Get available features list
   */
  getAvailableFeatures(): Array<{ name: keyof FeatureFlags; description: string }> {
    return [
      { name: 'beta_features', description: 'Access to beta features' },
      { name: 'advanced_analytics', description: 'Advanced analytics and insights' },
      { name: 'ai_assistant', description: 'AI-powered farming assistant' },
      { name: 'marketplace_access', description: 'Access to marketplace features' },
      { name: 'smart_farming', description: 'Smart farming tools and automation' },
      { name: 'carbon_credits', description: 'Carbon credits and sustainability features' },
      { name: 'logistics_tracking', description: 'Advanced logistics tracking' },
      { name: 'community_features', description: 'Community and social features' },
    ];
  }
}

// Export singleton instance
export const featureFlagService = FeatureFlagService.getInstance();

// React hook for feature flags
export const useFeatureFlag = (featureName: keyof FeatureFlags, userId?: string) => {
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkFeature = async () => {
      setLoading(true);
      const enabled = await featureFlagService.isFeatureEnabled(featureName, userId);
      setIsEnabled(enabled);
      setLoading(false);
    };

    checkFeature();
  }, [featureName, userId]);

  return { isEnabled, loading };
};

// Utility functions for common feature checks
export const isFeatureEnabled = async (featureName: keyof FeatureFlags, userId?: string) => {
  return featureFlagService.isFeatureEnabled(featureName, userId);
};

export const enableFeatureForUser = async (userId: string, featureName: keyof FeatureFlags) => {
  return featureFlagService.enableFeatureForUser(userId, featureName);
};

export const getUserPreferences = async (userId: string) => {
  return featureFlagService.getUserPreferences(userId);
};

export const isDemoModeEnabled = () => {
  const raw = import.meta.env.VITE_DEMO_MODE;
  return raw === 'true' || raw === '1';
};
